<script lang="ts" setup>
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';
import { ref, useTemplateRef } from 'vue';
import { getBuilderProjects, type GetProjectDetailsProjectResponse } from '@/api';
import BuilderProject from '@/views/projects/BuilderProject.vue';
import SectionHeader from '@/components/SectionHeader.vue';
import { useI18n } from 'vue-i18n';
import { useSsgState } from '@/utilities/ssg-state.ts';

const { project } = defineProps<{
  project: GetProjectDetailsProjectResponse;
}>();

const { t } = useI18n();

const builderProjectsContainer = useTemplateRef('builderProjectsContainer');
const isAtStart = ref(true);
const isAtEnd = ref(false);
const builderProjects = ref<number[]>([]);

const fetchBuilderProjects = async (): Promise<number[]> => {
  const fetch = await getBuilderProjects({
    path: {
      builderId: project.builder.id,
    },
    query: {
      projectId: project.id,
    },
  });
  if (fetch.response?.status == 200 && fetch.data) {
    return fetch.data.items;
  } else {
    throw Error(`Fetching search results failed: ${fetch.error}`);
  }
};

const updateScrollIndicators = () => {
  if (builderProjectsContainer.value) {
    const container = builderProjectsContainer.value;
    isAtStart.value = container.scrollLeft === 0;
    isAtEnd.value = Math.round(container.scrollWidth - container.scrollLeft) === container.clientWidth;
  }
};

const scrollBuilderProjects = (direction: number) => {
  if (builderProjectsContainer.value) {
    const scrollAmount = builderProjectsContainer.value.offsetWidth * 0.75;
    builderProjectsContainer.value.scrollBy({
      left: direction * scrollAmount,
      behavior: 'smooth',
    });
  }
};

const loadBuilderProjects = async () => {
  try {
    builderProjects.value = await fetchBuilderProjects();
  } catch (err) {
    console.error(err);
  }
};

useSsgState({
  key: `builder-projects:${project.id}`,
  refs: { builderProjects },
  isLoaded: () => builderProjects.value.length > 0,
  load: loadBuilderProjects,
});
</script>

<template>
  <section v-if="builderProjects.length > 0">
    <SectionHeader>{{ t('views.project.builder-projects.header', { builder: project.builder.name }) }}</SectionHeader>
    <div class="relative -top-2">
      <div class="no-print absolute top-0 -left-0.5 z-10 flex h-full items-center justify-center bg-linear-to-r from-white to-50% pl-3">
        <button
          :class="['hover:text-ph-green-300 focus:outline-ph-green-600/20 cursor-pointer rounded-md bg-gray-800/20 py-1 focus:outline-2 focus:outline-offset-2', !isAtStart ? 'text-gray-100' : 'hidden']"
          @click="scrollBuilderProjects(-1)"
        >
          <ChevronLeftIcon class="h-10 w-10" />
          <span class="sr-only">{{ t('views.project.builder-projects.scroll-left') }}</span>
        </button>
      </div>
      <ul
        ref="builderProjectsContainer"
        class="flex scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 flex-row gap-2 overflow-x-auto scroll-smooth px-1 pt-1 pb-2 md:scrollbar-none"
        role="list"
        @scroll="updateScrollIndicators"
      >
        <template v-for="(projectId, index) in builderProjects" :key="projectId">
          <BuilderProject :index="index" :project-id="projectId" />
        </template>
      </ul>
      <div class="no-print absolute top-0 -right-0.5 z-10 flex h-full items-center justify-center bg-linear-to-l from-white to-50% pr-3">
        <button
          :class="['hover:text-ph-green-300 focus:outline-ph-green-600/20 cursor-pointer rounded-md bg-gray-800/20 py-1 focus:outline-2 focus:outline-offset-2', !isAtEnd ? 'text-gray-100' : 'hidden']"
          @click="scrollBuilderProjects(1)"
        >
          <ChevronRightIcon class="h-10 w-10" />
          <span class="sr-only">{{ t('views.project.builder-projects.scroll-right') }}</span>
        </button>
      </div>
    </div>
  </section>
</template>

<style lang="css" scoped></style>
